import { P } from 'Core/Common/Promise';

const LOADING_STATUS = 'App_LoadingStatus';
const DOCUMENT_LOADED = 'Document_Loaded';
const SAVE_RESPONSE = 'Action_Save_Resp';
const UNO_COMMAND = 'Send_UNO_Command';
/** Collabora opens at 100%, which leaves the page small in the space it is given. There is no zoom in ui_defaults and no Action_Zoom in the postMessage API. .uno:ZoomOptimal and .uno:ZoomPageWidth are not in its bundle either - sending them did nothing at all - but .uno:ZoomPlus is, so the zoom is stepped up the way its own toolbar does it. */
const ZOOM_IN_COMMAND = '.uno:ZoomPlus';
/** Two steps: 100% -> ~120% -> ~140%, readable without pushing a portrait page wider than the frame. */
const ZOOM_IN_STEPS = 2;
const ZOOM_STEP_DELAY_MS = 250;
const ANNOUNCE_INTERVAL_MS = 500;
const ANNOUNCE_ATTEMPTS = 40;
/** Long enough for Collabora to push a real save through WOPI, short enough that a document it declined to save does not stall the user. */
const SAVE_SETTLE_MS = 4000;

/** Drives the Collabora editor embedded in the template designer. */
export class CollaboraFrame {
	private _frame: HTMLIFrameElement;
	private _isLoaded: boolean;
	/** A field code clicked before the document finished loading would be dropped silently. */
	private _pending: Array<() => void>;
	private _saveRequest: P.Deferred<void>;
	private _saveTimer: number;

	constructor(frame: HTMLIFrameElement) {
		this._frame = frame;
		this._isLoaded = false;
		this._saveTimer = null;
		this._pending = [];

		window.addEventListener('message', (event: MessageEvent) => this.OnMessage(event));
		this.AnnounceUntilHeard();
	}

	/** Collabora ignores every command from a host it has not been introduced to, and the introduction has to reach the loaded editor: this frame is still blank when we are built, and is navigated by a form POST straight after. */
	private AnnounceUntilHeard(): void {
		let attempts = 0;

		const announce = () => {
			if (attempts >= ANNOUNCE_ATTEMPTS) {
				window.clearInterval(handle);
				return;
			}

			attempts++;
			this.Send('Host_PostmessageReady', null);
		};

		const handle = window.setInterval(announce, ANNOUNCE_INTERVAL_MS);
		announce();
	}

	/** Collabora measures itself once and lays out for the size it found. */
	Relayout(): void {
		const relayout = () => {
			try {
				this._frame?.contentWindow?.dispatchEvent(new Event('resize'));
			} catch (e) {
			}
		};

		/** Twice: once for the new size, once after the browser has settled it - asking too early measures the box the editor is leaving. */
		requestAnimationFrame(relayout);
		window.setTimeout(relayout, 350);
	}

	Paste(text: string): void {
		this.Queue(() => this.Send('Action_Paste', {
			Mimetype: 'text/plain;charset=utf-8',
			Data: text
		}));
	}

	PasteHtml(html: string): void {
		this.Queue(() => this.Send('Action_Paste', { Mimetype: 'text/html', Data: html }));
	}

	/** Collabora saves on its own schedule; the platform saves when the user says so. */
	Save(): P.Promise<void> {
		if (this._saveRequest) {
			return this._saveRequest.promise();
		}

		this._saveRequest = P.defer<void>();
		this.Queue(() => {
			this.Send('Action_Save', {
				DontTerminateEdit: true,
				DontSaveIfUnmodified: false,
				Notify: true
			});

			this._saveTimer = window.setTimeout(() => this.CompleteSave(), SAVE_SETTLE_MS);
		});

		return this._saveRequest.promise();
	}

	private CompleteSave(rejection?: string): void {
		if (this._saveTimer) {
			window.clearTimeout(this._saveTimer);
			this._saveTimer = null;
		}

		if (!this._saveRequest) {
			return;
		}

		const request = this._saveRequest;
		this._saveRequest = null;

		if (rejection) {
			request.reject({ message: rejection });
		} else {
			request.resolve(null);
		}
	}

	private OnMessage(event: MessageEvent): void {
		if (!this._frame) {
			return;
		}

		let message: { MessageId: string, Values: any };
		try {
			message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
		} catch (e) {
			return;
		}

		if (!message) {
			return;
		}

		if (message.MessageId === LOADING_STATUS && message.Values && message.Values.Status === DOCUMENT_LOADED) {
			this._isLoaded = true;
			this.ApplyDefaultZoom();
			this._pending.forEach(action => action());
			this._pending = [];
			return;
		}

		if (message.MessageId === SAVE_RESPONSE && this._saveRequest) {
			const failed = message.Values && message.Values.success === false;
			this.CompleteSave(failed ? 'Collabora could not save the document' : null);
			return;
		}

	}

	private Queue(action: () => void): void {
		if (this._isLoaded) {
			action();
		} else {
			this._pending.push(action);
		}
	}

	/** Sent only after Document_Loaded: before that there is nothing to zoom and the command is dropped. Stepped rather than set, because a step is what the editor exposes. */
	private ApplyDefaultZoom(): void {
		for (let step = 0; step < ZOOM_IN_STEPS; step++) {
			setTimeout(() => this.Send(UNO_COMMAND, { Command: ZOOM_IN_COMMAND }), step * ZOOM_STEP_DELAY_MS);
		}
	}

	private Send(messageId: string, values: any): void {
		if (!this._frame || !this._frame.contentWindow) {
			return;
		}

		this._frame.contentWindow.postMessage(
			JSON.stringify({ MessageId: messageId, SendTime: Date.now(), Values: values }),
			'*');
	}
}
